<script lang="ts" setup>
import { onMounted, ref, computed } from "vue";
import { useRouter } from "vue-router";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import PageHeader from "@/components/PageHeader.vue";
import DataView from "@/components/DataView.vue";
import ErrorState from "@/views/shared/components/ErrorState.vue";
import { emailService } from '@/services/email.service';
import type { EmailTemplate } from '@/services/email.service';
import { ConnectionError } from '@/services/api';
import type { Config } from 'datatables.net';

const router = useRouter();

const templates = ref<EmailTemplate[]>([]);
const isLoading = ref(false);
const hasError = ref(false);
const errorType = ref<'connection' | 'http' | 'general'>('general');
const searchQuery = ref('');
const selectedType = ref<string>('all');
const selectedStatus = ref<string>('all');

const availableTypes = computed(() => {
  const types = new Set(templates.value.map(t => t.templateType));
  return Array.from(types).sort();
});

const filteredTemplates = computed(() => {
  return templates.value.filter((template) => {
    const matchesSearch =
      !searchQuery.value ||
      template.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      template.subject.toLowerCase().includes(searchQuery.value.toLowerCase());
    const matchesType = selectedType.value === 'all' || template.templateType === selectedType.value;
    const matchesStatus =
      selectedStatus.value === 'all' ||
      (selectedStatus.value === 'active' && template.isActive) ||
      (selectedStatus.value === 'inactive' && !template.isActive);
    return matchesSearch && matchesType && matchesStatus;
  });
});

const tableOptions = {
  columns: [
    {
      data: 'name',
      title: 'Template',
      render: (d: string, _: string, row: EmailTemplate) =>
        `<strong>${d}</strong><br><small class="text-muted">${row.description}</small>`,
    },
    {
      data: 'templateType',
      title: 'Type',
      render: (d: string) =>
        `<span class="badge bg-${emailService.getTemplateTypeBadgeColor(d)}">${d}</span>`,
    },
    {
      data: 'variables',
      title: 'Variables',
      render: (d: any[]) => `<span class="fw-bold">${d?.length || 0}</span>`,
    },
    {
      data: 'isActive',
      title: 'Status',
      render: (d: boolean) =>
        `<span class="badge text-uppercase ${d ? 'bg-success' : 'bg-secondary'}">${d ? 'Active' : 'Inactive'}</span>`,
    },
    {
      data: 'version',
      title: 'Version',
      render: (d: number) => `<small class="text-muted">v${d}</small>`,
    },
    {
      data: 'createdAt',
      title: 'Created',
      render: (d: string) => `<small class="text-muted">${new Date(d).toLocaleDateString()}</small>`,
    },
  ],
  // topEnd only — external search replaces DataTables'; info + paging stay (a stripped pager
  // silently truncates at 10 rows).
  layout: { topEnd: '' },
} as Config;

async function loadTemplates() {
  isLoading.value = true;
  hasError.value = false;
  try {
    templates.value = await emailService.listTemplates();
  } catch (error) {
    console.error('Error loading templates:', error);
    hasError.value = true;
    errorType.value = error instanceof ConnectionError ? 'connection' : 'general';
  } finally {
    isLoading.value = false;
  }
}

function createTemplate() {
  router.push('/admin/email-templates/new');
}

function editTemplate(id: string) {
  router.push(`/admin/email-templates/${id}`);
}

onMounted(async () => {
  await loadTemplates();
});
</script>

<template>
  <PanelGrid :columns="1">
    <PanelCard>
      <PageHeader
        title="Email Templates"
        :breadcrumbs="[
          { label: 'Dashboard', href: '/' },
          { label: 'Email Templates' },
        ]"
      >
        <template #actions>
          <button class="bb-btn-primary-cta" @click="createTemplate">
            <i class="bi bi-plus-circle me-2"></i>New Template
          </button>
        </template>
      </PageHeader>

      <!-- Filters -->
      <div class="row g-3 mb-4">
        <div class="col-12 col-md-6">
          <input
            v-model="searchQuery"
            type="text"
            class="form-control"
            placeholder="Search templates..."
          />
        </div>
        <div class="col-6 col-md-3">
          <select v-model="selectedType" class="form-select">
            <option value="all">All Types</option>
            <option v-for="type in availableTypes" :key="type" :value="type">
              {{ type }}
            </option>
          </select>
        </div>
        <div class="col-6 col-md-3">
          <select v-model="selectedStatus" class="form-select">
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Error State -->
      <ErrorState v-else-if="hasError" :type="errorType" @retry="loadTemplates" />

      <!-- Data -->
      <DataView
        v-else
        :data="filteredTemplates"
        :view-mode="'table'"
        :options="tableOptions"
        @row-click="(item) => editTemplate(item.id)"
      >
        <!-- Mobile / grid card (overrides the generic label:value list) -->
        <template #grid-card="{ row }">
          <div class="card h-100 shadow-sm card-hover">
            <div class="card-body d-flex flex-column gap-2">
              <!-- Title + status -->
              <div class="d-flex align-items-start justify-content-between gap-2">
                <h5 class="mb-0 text-truncate">{{ row.name }}</h5>
                <span
                  class="badge text-uppercase flex-shrink-0"
                  :class="row.isActive ? 'bg-success' : 'bg-secondary'"
                >{{ row.isActive ? 'Active' : 'Inactive' }}</span>
              </div>

              <!-- Description -->
              <p v-if="row.description" class="small text-muted mb-0 text-truncate">
                {{ row.description }}
              </p>

              <!-- Meta row: type, variables, version, created -->
              <div class="d-flex flex-wrap align-items-center gap-2 mt-auto pt-2 small text-muted">
                <span :class="`badge bg-${emailService.getTemplateTypeBadgeColor(row.templateType)}`">
                  {{ row.templateType }}
                </span>
                <span>{{ row.variables?.length || 0 }} vars</span>
                <span class="text-body-tertiary">·</span>
                <span>v{{ row.version }}</span>
                <span class="ms-auto">Created: {{ new Date(row.createdAt).toLocaleDateString() }}</span>
              </div>
            </div>
          </div>
        </template>

        <template #empty-state>
          <div class="text-center py-5">
            <i class="bi bi-envelope-x display-1 text-muted"></i>
            <h3 class="mt-3 text-muted">No templates found</h3>
            <button class="bb-btn-primary-cta mt-2" @click="createTemplate">
              <i class="bi bi-plus me-1"></i>New Template
            </button>
          </div>
        </template>
      </DataView>
    </PanelCard>
  </PanelGrid>
</template>
